import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

import { HUNTING_PRODUCT_MEDIA_ROOT } from '../../shared/services/file.service';
import type { MediaFileEntry, MediaTransferProgress, MediaTransport } from '../../shared/services/media-transport/media-transport.port';

import { MOCK_HUNTING_DEVICE_MEDIA } from './mock-hunting-device-media.fixture';
import type { MockHuntingDeviceFile } from './mock-hunting-device-media.fixture';

/**
 * Paths are relative to the transport root, as in `WebDavTransport` (`list('')` = month dirs).
 * The bundled asset bytes must pass the engine's JPEG/MP4 integrity check.
 */
// ~1.2 s per file: long enough to read the downloading tile and watch the bar fill, short enough
// that a full nine-file run still finishes in about ten seconds.
const MOCK_PROGRESS_STEPS = 8;
const MOCK_PROGRESS_STEP_MS = 150;

@Injectable()
export class MockHuntingMediaTransport implements MediaTransport {
  // Assets never change at runtime — fetch each one once and reuse the cached blob for both
  // size$() (called twice, 2s apart, and must return the SAME value both times) and downloadToFile$().
  private readonly blobCache = new Map<string, Promise<Blob>>();

  async list$(remoteDirectory: string): Promise<Array<MediaFileEntry>> {
    if (remoteDirectory === '') {
      const monthDirectories = Array.from(new Set(MOCK_HUNTING_DEVICE_MEDIA.map(item => item.monthDir)));
      return monthDirectories.map(name => ({ name, size: 0, isDirectory: true }));
    }

    const filesInMonth = MOCK_HUNTING_DEVICE_MEDIA.filter(item => item.monthDir === remoteDirectory);
    return Promise.all(
      filesInMonth.map(async (item): Promise<MediaFileEntry> => {
        const blob = await this.fetchAssetBlob$(item.assetPath);
        return { name: item.deviceFileName, size: blob.size, isDirectory: false };
      })
    );
  }

  async size$(remotePath: string): Promise<number> {
    const entry = this.resolveEntry(remotePath);
    const blob = await this.fetchAssetBlob$(entry.assetPath);
    return blob.size;
  }

  async downloadToFile$(remotePath: string, localPath: string, onProgress?: (progress: MediaTransferProgress) => void): Promise<void> {
    const entry = this.resolveEntry(remotePath);
    const blob = await this.fetchAssetBlob$(entry.assetPath);

    for (let step = 1; step < MOCK_PROGRESS_STEPS; step++) {
      await new Promise(resolve => setTimeout(resolve, MOCK_PROGRESS_STEP_MS));
      const bytes = Math.round((blob.size * step) / MOCK_PROGRESS_STEPS);
      const elapsedMs = step * MOCK_PROGRESS_STEP_MS;
      const mbPerSec = bytes / 1024 / 1024 / (elapsedMs / 1000);
      onProgress?.({ bytes, contentLength: blob.size, elapsedMs, mbPerSecAvg: mbPerSec, mbPerSecWindow: mbPerSec });
    }

    await this.writeLocalFile$(localPath, blob);
    onProgress?.({ bytes: blob.size, contentLength: blob.size, elapsedMs: MOCK_PROGRESS_STEPS * MOCK_PROGRESS_STEP_MS, mbPerSecAvg: 0, mbPerSecWindow: 0 });
  }

  isReachable$(): Promise<boolean> {
    return Promise.resolve(true);
  }

  // The engine resolves the target through Filesystem.getUri() before calling us, so `localPath` is
  // an absolute URI. Native writes take it as-is.
  // `recursive: true`: nothing upstream creates the hunting-products/<serial>/<yyyy-mm>/ folder.
  private async writeLocalFile$(localPath: string, blob: Blob): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Filesystem.writeFile({ path: localPath, data: await this.blobToBase64$(blob), recursive: true });
      return;
    }

    // The web backend cannot create the directory chain an absolute URI implies (it walks up to the
    // root and throws "Cannot create Root directory"), so write the relative portion against
    // Directory.Data — the same root HuntingProductMediaStore reads from. It also records a file's
    // size as the raw `data.length`, so a base64 string would store ~4/3 of the real byte count and
    // fail the engine's size check; a Blob records its own size.
    const rootIndex = localPath.indexOf(`${HUNTING_PRODUCT_MEDIA_ROOT}/`);
    if (rootIndex === -1) {
      throw new Error(`MockHuntingMediaTransport: local path "${localPath}" is not under "${HUNTING_PRODUCT_MEDIA_ROOT}/"`);
    }
    await Filesystem.writeFile({ path: localPath.slice(rootIndex), data: blob, directory: Directory.Data, recursive: true });
  }

  private resolveEntry(remotePath: string): MockHuntingDeviceFile {
    const fileName = remotePath.slice(remotePath.lastIndexOf('/') + 1);
    const entry = MOCK_HUNTING_DEVICE_MEDIA.find(item => item.deviceFileName === fileName);
    if (!entry) {
      throw new Error(`MockHuntingMediaTransport: no bundled asset for remote path "${remotePath}"`);
    }
    return entry;
  }

  private fetchAssetBlob$(assetPath: string): Promise<Blob> {
    let cached = this.blobCache.get(assetPath);
    if (!cached) {
      cached = fetch(assetPath).then(response => {
        if (!response.ok) {
          throw new Error(`MockHuntingMediaTransport: failed to fetch bundled asset "${assetPath}" (${response.status})`);
        }
        return response.blob();
      });
      this.blobCache.set(assetPath, cached);
    }
    return cached;
  }

  private blobToBase64$(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('loadend', () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        resolve(commaIndex === -1 ? result : result.slice(commaIndex + 1));
      });
      reader.addEventListener('error', () => reject(reader.error ?? new Error('MockHuntingMediaTransport: failed to read bundled asset')));
      reader.readAsDataURL(blob);
    });
  }
}
